import useLgMediaQuery from "@/src/hooks/media-queries/use-lg-media-query";
import useMdMediaQuery from "@/src/hooks/media-queries/use-md-media-query";
import { BlocksApiNews } from "@/src/models/news.model";
import { router } from "expo-router";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, FlatList, Image, Text, View } from "react-native";
import { NewsGridSkeleton } from "../loading-skeleton/news-card-skeleton";
import { AllNewsCard, NewsCardVariant } from "./all-news-cards";

type AllNewsGridProps = Readonly<{
  articles: BlocksApiNews[];
  isLoading: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  searchText: string;
  variant?: NewsCardVariant;
  isSaved: (id: string) => boolean;
  isOwner: (newsItem: BlocksApiNews) => boolean;
  isRead?: (id: string) => boolean;
  resolveArticleCategoryNames: (newsItem: BlocksApiNews) => string[];
  onSave: (itemId: string) => void;
  onEdit: (newsItem: BlocksApiNews) => void;
  onDelete: (newsItem: BlocksApiNews) => void;
  onEndReached?: () => void;
  onPin: (newsItem: BlocksApiNews) => void;
}>;

export default function AllNewsGrid({
  articles,
  isLoading,
  isFetchingNextPage = false,
  hasNextPage = false,
  searchText,
  variant = "all",
  isSaved,
  isOwner,
  isRead,
  resolveArticleCategoryNames,
  onSave,
  onEdit,
  onPin,
  onDelete,
  onEndReached,
}: AllNewsGridProps) {
  const { t } = useTranslation();
  const isMd = useMdMediaQuery();
  const isLg = useLgMediaQuery();

  const numColumns = isLg ? 3 : isMd ? 2 : 1;
  const itemWidthClass =
    numColumns === 3 ? "w-[32.4%]" : numColumns === 2 ? "w-[49%]" : "w-full";
  const columnWrapperStyle = numColumns > 1 ? { gap: 10 } : undefined;

  const handleCardPress = (newsItem: BlocksApiNews) => {
    if (variant === "draft") {
      router.push({
        pathname: "/(tabs)/news/news-creation",
        params: {
          draftId: newsItem.ItemId,
          Title: newsItem.Title,
          Content: newsItem.Content,
          categoryIds: JSON.stringify(newsItem.categoryIds ?? []),
          coverImageUrl: newsItem.coverImageUrl ?? "",
          attachments: JSON.stringify(newsItem.attachmentUrls ?? []),
          // Carried for the same reason as the other two edit entry points —
          // the form treats a missing param as "not pinned" and saves that.
          isPinned: String(newsItem.isPinned ?? false),
          // This card only appears in the draft grid, so this is always
          // "true" — carried anyway so the creation screen's "Save as Draft"
          // visibility check doesn't depend on which entry point was used.
          isDraft: "true",
        },
      });
    } else {
      router.push({
        pathname: "/(tabs)/news/news-detail",
        params: { newsId: newsItem.ItemId },
      });
    }
  };

  if (isLoading) return <NewsGridSkeleton columns={numColumns} />;

  if (!articles.length) {
    return (
      <View className="flex-1 items-center justify-center gap-4">
        <Image
          source={require("@/src/assets/images/empty-state.png")}
          style={{ width: 100, height: 100 }}
          resizeMode="contain"
        />
        <Text className="text-onyx text-sm">
          {searchText.trim()
            ? t("HINT_NEWS_FILTER")
            : variant === "draft"
              ? t("LABEL_NO_DRAFTS")
              : variant === "saved"
                ? t("LABEL_NO_SAVED_NEWS")
                : t("LABEL_NO_NEWS")}
        </Text>
      </View>
    );
  }

  return (
    <View className="flex-1 pt-4">
      <FlatList
        key={numColumns}
        data={articles}
        keyExtractor={(newsItem) => newsItem.ItemId}
        numColumns={numColumns}
        columnWrapperStyle={columnWrapperStyle}
        contentContainerStyle={{ gap: 12, paddingBottom: 16 }}
        showsVerticalScrollIndicator={false}
        onEndReached={() => {
          if (hasNextPage && !isFetchingNextPage && onEndReached) {
            onEndReached();
          }
        }}
        onEndReachedThreshold={0.3}
        ListFooterComponent={
          isFetchingNextPage ? (
            <View className="py-4 items-center">
              <ActivityIndicator size="small" />
            </View>
          ) : null
        }
        renderItem={({ item: newsItem }) => (
          <AllNewsCard
            newsItem={newsItem}
            categoryNames={resolveArticleCategoryNames(newsItem)}
            isSaved={isSaved(newsItem.ItemId)}
            isPinned={newsItem.isPinned ?? false}
            isOwner={isOwner(newsItem)}
            isNew={variant === "pinned" && !(isRead?.(newsItem.ItemId) ?? true)}
            variant={variant}
            itemWidthClass={itemWidthClass}
            onPress={() => handleCardPress(newsItem)}
            onSave={() => onSave(newsItem.ItemId)}
            onEdit={() => onEdit(newsItem)}
            onPin={() => onPin(newsItem)}
            onDelete={() => onDelete(newsItem)}
          />
        )}
      />
    </View>
  );
}
